'use client'

/**
 * In-preview overlay for SiteSmith Page-mode section edits + Chrome v2
 * direct-manipulation chrome edits (T4 of SiteSmith Builder Rethink).
 *
 * Mounts only when the storefront's middleware has set the
 * `x-sitesmith-edit` header (i.e. a valid `?siteSmithEdit=<token>`
 * query param landed on the request). The server-side gate is the
 * source of truth — this client component additionally re-validates
 * via a cross-origin POST to the portal's `preview-edit-check` action
 * so an expired or tampered token can't show the overlay even if the
 * header somehow leaked through.
 *
 * UX:
 *   1. Hash-sealed token + portal admin URL come from props.
 *   2. On mount: POST {token} → portal/api/site-builder?action=preview-edit-check.
 *      If `ok: false`, hide silently.
 *   3. Scan DOM for `[data-section-ref]` AND `[data-chrome-target]` elements.
 *      For each, attach a hover affordance + alt-click handler.
 *   4. Section-ref alt-click → opens the prior popover with deep-link
 *      "Edit this section" / "Add flourish" CTAs into the admin portal.
 *   5. Chrome-target alt-click → opens an in-place edit popover with
 *      target-appropriate fields (label / href / boolean toggle). Save
 *      calls `chrome-slot-edit` on the portal with the slot-keyed delta;
 *      success busts the storefront ISR tag for this tenant.
 *
 *      Targets that don't have a chrome-data home (logo image, nav-item
 *      labels, utility-bar item labels, footer column-header labels) fall
 *      through to an "Edit in admin" deep link — these settings live in
 *      `storefront_settings` and `tenant_brand_assets`, not in the chrome
 *      sentinel-row payload that `chrome-slot-edit` writes.
 *
 *   6. Sections with `data-section-protected` are rendered with a
 *      disabled affordance + tooltip ("Protected zone — edit in admin").
 */

import { useEffect, useRef, useState, useCallback } from 'react'
// Per-file subpath, not the `/registry` barrel: this is a 'use client'
// module, and the barrel would drag COMPONENT_REGISTRY + every per-kind
// zod schema into the browser bundle. bindingPath.ts is dependency-free.
import { resolveBindingKey } from '@sifly/agent-foundation/registry/bindingPath'
import { siteBuilderEndpoint } from '@/lib/siteBuilderEndpoint'

type Props = {
  token: string
  portalUrl: string
}

type SectionRef = {
  element: HTMLElement
  /** Page path the section lives on, or the literal 'chrome' for header/footer. */
  pagePath: string
  /** Section index within the page composition. For chrome refs this is -1. */
  index: number
  /** Section kind (e.g. 'hero', 'cta_band'); for chrome refs, the variant. */
  kind: string
  protected: boolean
  /** True when the ref is a chrome target (`chrome:header:<variant>` etc.). */
  isChrome: boolean
  /** 'header' | 'footer' when `isChrome` is true. */
  chromeTarget?: 'header' | 'footer'
}

type ChromeTargetRef = {
  element: HTMLElement
  /** The raw data-chrome-target attribute value. Examples:
   * `header:logo`, `header:cta`, `header:nav_item:abc`,
   * `header:utility_item:abc`, `footer:tagline`,
   * `footer:column_header:abc`, `footer:newsletter`. */
  raw: string
  region: 'header' | 'footer'
  /** The kind after region. `logo` / `cta` / `nav_item` / `utility_item`
   * / `tagline` / `column_header` / `newsletter` / `utility_bar`. */
  kind: string
  /** Optional sub-id for collection items (nav_item, utility_item,
   * column_header). */
  id?: string
}

// Stream H / Phase F — inline section-body editing.
type EditorBinding =
  | { type: 'text'; max: number; multiline?: boolean }
  | { type: 'url'; max?: number }
  | { type: 'media'; accept: 'image' | 'video' | 'document' | 'any' }
  | { type: 'select'; options: readonly string[] }
  | { type: 'switch' }
  | { type: 'number'; min?: number; max?: number; integer?: boolean }

type FieldEditRef = {
  element: HTMLElement
  /** content_pages.id from the nearest [data-page-id] ancestor. */
  pageId: string
  /** Section index within the page composition. */
  sectionIdx: number
  /** Section kind (e.g. 'hero'). */
  kind: string
  /** Dotted field path under data.* (e.g. 'data.slides.0.title'). */
  fieldPath: string
  binding: EditorBinding
}

function readBindingsCatalog(): Record<string, Record<string, EditorBinding>> {
  if (typeof document === 'undefined') return {}
  const el = document.getElementById('sitesmith-editor-bindings')
  if (!el || !el.textContent) return {}
  try {
    return JSON.parse(el.textContent) as Record<string, Record<string, EditorBinding>>
  } catch {
    return {}
  }
}

function isMobileViewport(): boolean {
  if (typeof window === 'undefined') return false
  return window.innerWidth < 768
}

export default function SiteSmithEditOverlay({ token, portalUrl }: Props) {
  const [verified, setVerified] = useState<boolean | null>(null)
  const [activeSection, setActiveSection] = useState<SectionRef | null>(null)
  const [activeChrome, setActiveChrome] = useState<ChromeTargetRef | null>(null)
  const [activeField, setActiveField] = useState<FieldEditRef | null>(null)
  const [mobileHint, setMobileHint] = useState(false)
  const bindingsRef = useRef<Record<string, Record<string, EditorBinding>>>({})

  useEffect(() => {
    let cancelled = false
    fetch(siteBuilderEndpoint(portalUrl, 'preview-edit-check'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'preview-edit-check', token }),
    })
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return
        setVerified(!!j.ok)
      })
      .catch(() => {
        if (cancelled) return
        setVerified(false)
      })
    return () => {
      cancelled = true
    }
  }, [token, portalUrl])

  useEffect(() => {
    if (!verified) return
    bindingsRef.current = readBindingsCatalog()
    const cleanups: Array<() => void> = []

    // Stream H — inline section-body field edits. Walks [data-field]
    // elements inside [data-section-ref] and attaches alt-click handlers.
    const wireFieldClicks = () => {
      document.querySelectorAll<HTMLElement>('[data-field]').forEach((el) => {
        if (el.dataset.sitesmithFieldWired === '1') return
        const sectionEl = el.closest<HTMLElement>('[data-section-ref]')
        if (!sectionEl) return
        const ref = parseSectionRef(sectionEl)
        if (!ref || ref.isChrome) return
        const fieldPath = el.dataset.field!
        const kindBindings = bindingsRef.current[ref.kind]
        if (!kindBindings) return
        // Exact key first, then the generalized `data.cards.*.title` form.
        // A bare `kindBindings[fieldPath]` lookup left every repeated
        // element past index 0 un-editable — the renderer marks all of
        // them, the map only listed the first, and this returned silently.
        const bindingKey = resolveBindingKey(fieldPath, kindBindings)
        if (!bindingKey) return
        const binding = kindBindings[bindingKey]
        // Page id sits on the section element via data-page-id, when set
        // by the renderer. We resolve it below in the click handler so we
        // can tolerate missing attribute (degraded path).
        el.classList.add('sitesmith-field-target')
        el.dataset.sitesmithFieldWired = '1'
        const handler = (e: MouseEvent) => {
          if (!e.altKey) return
          if (isMobileViewport()) {
            setMobileHint(true)
            return
          }
          e.preventDefault()
          e.stopPropagation()
          const pageId =
            el.closest<HTMLElement>('[data-page-id]')?.dataset.pageId ||
            sectionEl.dataset.pageId ||
            ''
          if (!pageId) {
            console.warn('[sitesmith] no data-page-id ancestor for', fieldPath)
            return
          }
          setActiveField({
            element: el,
            pageId,
            sectionIdx: ref.index,
            kind: ref.kind,
            fieldPath,
            binding,
          })
          setActiveSection(null)
          setActiveChrome(null)
        }
        el.addEventListener('click', handler)
        cleanups.push(() => {
          el.removeEventListener('click', handler)
          el.classList.remove('sitesmith-field-target')
          delete el.dataset.sitesmithFieldWired
        })
      })
    }
    wireFieldClicks()

    // Section refs — alt-click opens deep-link popover (existing behavior).
    document.querySelectorAll<HTMLElement>('[data-section-ref]').forEach((el) => {
      el.classList.add('sitesmith-edit-target')
      const handler = (e: MouseEvent) => {
        if (!e.altKey) return
        // Don't fire when the alt-click landed on a chrome target or a
        // field target nested inside the section; those get their own
        // handlers above/below.
        const tgt = e.target as HTMLElement | null
        if (tgt && (tgt.closest('[data-chrome-target]') || tgt.closest('[data-field]'))) return
        e.preventDefault()
        e.stopPropagation()
        const ref = parseSectionRef(el)
        if (ref) {
          setActiveSection(ref)
          setActiveChrome(null)
        }
      }
      el.addEventListener('click', handler)
      cleanups.push(() => {
        el.removeEventListener('click', handler)
        el.classList.remove('sitesmith-edit-target')
      })
    })

    // Chrome targets — alt-click opens in-place edit popover (T4).
    document.querySelectorAll<HTMLElement>('[data-chrome-target]').forEach((el) => {
      el.classList.add('sitesmith-chrome-target')
      const handler = (e: MouseEvent) => {
        if (!e.altKey) return
        e.preventDefault()
        e.stopPropagation()
        const ref = parseChromeTargetRef(el)
        if (ref) {
          setActiveChrome(ref)
          setActiveSection(null)
        }
      }
      el.addEventListener('click', handler)
      cleanups.push(() => {
        el.removeEventListener('click', handler)
        el.classList.remove('sitesmith-chrome-target')
      })
    })

    return () => {
      cleanups.forEach((fn) => fn())
    }
  }, [verified])

  if (verified !== true) return null

  return (
    <>
      <style>{`
        .sitesmith-edit-target { outline: 1px dashed rgba(124, 58, 237, 0.0); outline-offset: 4px; transition: outline-color 120ms; }
        .sitesmith-edit-target:hover { outline-color: rgba(124, 58, 237, 0.6); cursor: alias; }
        .sitesmith-chrome-target { outline: 1px dashed rgba(16, 185, 129, 0.0); outline-offset: 2px; transition: outline-color 120ms; }
        .sitesmith-chrome-target:hover { outline-color: rgba(16, 185, 129, 0.7); cursor: alias; }
        .sitesmith-field-target { outline: 1px dashed rgba(59, 130, 246, 0.0); outline-offset: 2px; transition: outline-color 120ms; border-radius: 2px; }
        .sitesmith-field-target:hover { outline-color: rgba(59, 130, 246, 0.8); cursor: text; background-color: rgba(59, 130, 246, 0.04); }
      `}</style>
      <div
        style={{
          position: 'fixed',
          top: 12,
          right: 12,
          zIndex: 9999,
          background: 'rgba(124, 58, 237, 0.95)',
          color: 'white',
          padding: '8px 12px',
          borderRadius: 6,
          fontSize: 12,
          fontFamily: 'system-ui, sans-serif',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          pointerEvents: 'none',
        }}
      >
        SiteSmith edit mode — Alt-click any section or chrome element
      </div>
      {activeSection && (
        <SectionEditPopover
          sectionRef={activeSection}
          portalUrl={portalUrl}
          onClose={() => setActiveSection(null)}
        />
      )}
      {activeChrome && (
        <ChromeEditPopover
          chromeRef={activeChrome}
          token={token}
          portalUrl={portalUrl}
          onClose={() => setActiveChrome(null)}
        />
      )}
      {activeField && (
        <FieldEditPopover
          fieldRef={activeField}
          token={token}
          portalUrl={portalUrl}
          onClose={() => setActiveField(null)}
        />
      )}
      {mobileHint && (
        // Admin-only transient hint: role="alert" announces it; tap-to-dismiss
        // (the text says so) is a pointer convenience on a non-interactive toast.
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
        <div
          role="alert"
          onClick={() => setMobileHint(false)}
          style={{
            position: 'fixed',
            top: 56,
            right: 12,
            zIndex: 9999,
            background: 'rgba(31, 41, 55, 0.95)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: 6,
            fontSize: 12,
            maxWidth: 240,
            cursor: 'pointer',
          }}
        >
          Inline editing is desktop-only. Switch to a wider viewport to edit
          on canvas. (Tap to dismiss)
        </div>
      )}
    </>
  )
}

function parseSectionRef(el: HTMLElement): SectionRef | null {
  const ref = el.dataset.sectionRef
  if (!ref) return null
  // Chrome targets — shape "chrome:header:<variant>" or "chrome:footer:<variant>".
  // SiteSmith Chrome mode (Phase 5). Deep-links open the Chrome tab in the portal.
  if (ref.startsWith('chrome:')) {
    const parts = ref.split(':')
    if (parts.length < 3) return null
    const targetSeg = parts[1]
    if (targetSeg !== 'header' && targetSeg !== 'footer') return null
    const variant = parts.slice(2).join(':')
    return {
      element: el,
      pagePath: 'chrome',
      index: -1,
      kind: variant,
      protected: el.dataset.sectionProtected === '1',
      isChrome: true,
      chromeTarget: targetSeg,
    }
  }
  // Section refs — "<page_path>:<index>:<kind>".
  // page_path may itself contain colons in URL-encoded form; split from the right.
  const parts = ref.split(':')
  if (parts.length < 3) return null
  const kind = parts[parts.length - 1]
  const indexStr = parts[parts.length - 2]
  const pagePath = parts.slice(0, parts.length - 2).join(':')
  const index = Number(indexStr)
  if (!Number.isFinite(index)) return null
  return {
    element: el,
    pagePath,
    index,
    kind,
    protected: el.dataset.sectionProtected === '1',
    isChrome: false,
  }
}

function parseChromeTargetRef(el: HTMLElement): ChromeTargetRef | null {
  const raw = el.dataset.chromeTarget
  if (!raw) return null
  const parts = raw.split(':')
  if (parts.length < 2) return null
  const region = parts[0]
  if (region !== 'header' && region !== 'footer') return null
  const kind = parts[1]
  const id = parts.length > 2 ? parts.slice(2).join(':') : undefined
  return { element: el, raw, region, kind, id }
}

function SectionEditPopover({
  sectionRef,
  portalUrl,
  onClose,
}: {
  sectionRef: SectionRef
  portalUrl: string
  onClose: () => void
}) {
  const rect = sectionRef.element.getBoundingClientRect()
  const target = sectionRef.isChrome
    ? sectionRef.chromeTarget ?? 'header'
    : encodeURIComponent(
        JSON.stringify({
          kind: 'section',
          page_path: sectionRef.pagePath,
          section_index: sectionRef.index,
          section_kind: sectionRef.kind,
        }),
      )
  const editUrl = sectionRef.isChrome
    ? `${portalUrl}/admin/website-builder#sitesmith-chrome=${target}`
    : `${portalUrl}/admin/website-builder#sitesmith-edit=${target}`
  const flourishUrl = sectionRef.isChrome
    ? editUrl
    : `${portalUrl}/admin/website-builder#sitesmith-edit=${target}&flourish=1`

  const top = Math.max(8, rect.top + window.scrollY - 8)
  const left = Math.max(8, Math.min(rect.left + window.scrollX, window.innerWidth - 280))

  return (
    <div
      role="dialog"
      aria-label={`Edit ${sectionRef.isChrome ? sectionRef.chromeTarget : sectionRef.kind}`}
      style={{
        position: 'absolute',
        top,
        left,
        zIndex: 10000,
        background: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: 6,
        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        padding: 12,
        fontSize: 13,
        fontFamily: 'system-ui, sans-serif',
        width: 260,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4, color: '#1f2937' }}>
        {sectionRef.isChrome ? (
          <>
            {sectionRef.chromeTarget}{' '}
            <span style={{ fontWeight: 400, color: '#6b7280' }}>
              @ {sectionRef.kind}
            </span>
          </>
        ) : (
          <>
            {sectionRef.kind}{' '}
            <span style={{ fontWeight: 400, color: '#6b7280' }}>
              @ {sectionRef.pagePath}:{sectionRef.index}
            </span>
          </>
        )}
      </div>
      {sectionRef.protected ? (
        <div style={{ color: '#9ca3af', fontSize: 12, padding: '6px 0' }}>
          Protected zone — edit in admin only.
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <a
            href={editUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              flex: 1,
              padding: '6px 10px',
              background: 'rgb(124, 58, 237)',
              color: 'white',
              borderRadius: 4,
              textAlign: 'center',
              textDecoration: 'none',
            }}
          >
            Edit
          </a>
          {!sectionRef.isChrome && (
            <a
              href={flourishUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                flex: 1,
                padding: '6px 10px',
                background: 'white',
                color: 'rgb(124, 58, 237)',
                border: '1px solid rgb(124, 58, 237)',
                borderRadius: 4,
                textAlign: 'center',
                textDecoration: 'none',
              }}
            >
              Flourish
            </a>
          )}
        </div>
      )}
      <button
        onClick={onClose}
        style={{
          marginTop: 8,
          width: '100%',
          padding: '4px 8px',
          background: 'transparent',
          border: 'none',
          color: '#6b7280',
          fontSize: 12,
          cursor: 'pointer',
        }}
      >
        Close
      </button>
    </div>
  )
}

// ─── Chrome edit popover (T4) ────────────────────────────────────────────

type ChromeFieldKind = 'string' | 'href' | 'boolean'

type ChromeFieldSpec = {
  label: string
  slotPath: string
  kind: ChromeFieldKind
}

/**
 * Field specs by chrome-target kind. Each spec maps to a slot in the
 * chrome override payload's `data` object that `chrome-slot-edit`
 * accepts. Targets not listed here (logo, nav_item, utility_item,
 * column_header, utility_bar) live outside the chrome payload — those
 * fall through to a deep-link to the admin portal.
 */
const CHROME_FIELD_SPECS: Record<string, ChromeFieldSpec[]> = {
  // header
  'header:cta': [
    { label: 'Label', slotPath: 'cta.label', kind: 'string' },
    { label: 'Href', slotPath: 'cta.href', kind: 'href' },
    { label: 'Enabled', slotPath: 'cta.enabled', kind: 'boolean' },
  ],
  // footer
  'footer:tagline': [{ label: 'Tagline', slotPath: 'tagline', kind: 'string' }],
  'footer:newsletter': [
    { label: 'Headline', slotPath: 'newsletter.headline', kind: 'string' },
    { label: 'Placeholder', slotPath: 'newsletter.placeholder', kind: 'string' },
    { label: 'Button label', slotPath: 'newsletter.cta_label', kind: 'string' },
    { label: 'Enabled', slotPath: 'newsletter.enabled', kind: 'boolean' },
  ],
}

function fieldSpecsFor(ref: ChromeTargetRef): ChromeFieldSpec[] | null {
  const key = `${ref.region}:${ref.kind}`
  return CHROME_FIELD_SPECS[key] ?? null
}

function isPhoneLike(value: string): boolean {
  // Loose: 7+ digits when stripped of formatting.
  const stripped = value.replace(/[^\d]/g, '')
  return stripped.length >= 7 && stripped.length <= 15
}

function ChromeEditPopover({
  chromeRef,
  token,
  portalUrl,
  onClose,
}: {
  chromeRef: ChromeTargetRef
  token: string
  portalUrl: string
  onClose: () => void
}) {
  const specs = fieldSpecsFor(chromeRef)
  const firstInputRef = useRef<HTMLInputElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const [values, setValues] = useState<Record<string, string | boolean>>(() => {
    if (!specs) return {}
    // Seed from the live element's visible text where we can.
    const text = chromeRef.element.innerText?.trim() ?? ''
    const seed: Record<string, string | boolean> = {}
    for (const s of specs) {
      if (s.kind === 'boolean') seed[s.slotPath] = true
      else if (s.slotPath.endsWith('label') || s.slotPath === 'tagline' || s.slotPath.endsWith('headline'))
        seed[s.slotPath] = text
      else seed[s.slotPath] = ''
    }
    return seed
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [staleNotice, setStaleNotice] = useState<string | null>(null)

  const rect = chromeRef.element.getBoundingClientRect()
  const top = Math.max(8, rect.bottom + window.scrollY + 6)
  const left = Math.max(8, Math.min(rect.left + window.scrollX, window.innerWidth - 300))

  // Focus first input on mount + ESC to close + Cmd/Ctrl+S to save
  // (Design Pass 6 accessibility).
  useEffect(() => {
    firstInputRef.current?.focus()
  }, [])

  const save = useCallback(async () => {
    if (!specs || saving || staleNotice) return
    setSaving(true)
    setError(null)
    try {
      // One slot-edit POST per field that changed from its seed. This
      // keeps the slot_path allowlist 1:1 with what the user actually
      // touched — no spurious writes when they only edited Label.
      let stale: string | null = null
      for (const s of specs) {
        const v = values[s.slotPath]
        const res = await fetch(siteBuilderEndpoint(portalUrl, 'chrome-slot-edit'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'chrome-slot-edit',
            token,
            region: chromeRef.region,
            slot_path: s.slotPath,
            value: v,
          }),
        })
        const json = (await res.json()) as { error?: string; revalidate?: RevalidateResult }
        if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`)
        // Every write fires its own tenant-tag flush and the tag is
        // tenant-global, so the LAST flush covers all prior writes — only
        // its outcome decides the warning.
        stale = staleCacheWarning(json.revalidate)
      }
      if (stale) {
        // Writes persisted; hold the popover open so the admin reads the
        // cache gap before triggering the reload themselves.
        setStaleNotice(stale)
        return
      }
      onClose()
      // Hard-refresh so the storefront picks up the new override (the
      // server-side ISR tag was busted by the API; reload re-fetches).
      window.location.reload()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }, [specs, saving, staleNotice, values, token, portalUrl, chromeRef.region, onClose])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault()
        void save()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [save, onClose])

  // Fallback for chrome targets that don't have a chrome-data slot
  // (logo, nav_item, utility_item, column_header, utility_bar). These
  // settings live in `storefront_settings` / `tenant_brand_assets` and
  // need a different write pipeline. Surface a deep-link instead of
  // silently failing.
  if (!specs) {
    const hint =
      chromeRef.kind === 'logo'
        ? 'Logo image and link live under Settings → Brand assets.'
        : chromeRef.kind === 'nav_item' || chromeRef.kind === 'utility_item' || chromeRef.kind === 'utility_bar'
          ? 'Nav and utility-bar items live under Settings → Navigation.'
          : chromeRef.kind === 'column_header'
            ? 'Footer column headers live under Settings → Navigation → Footer links.'
            : 'This element lives outside the chrome variant payload — edit it in admin.'
    return (
      <div
        ref={popoverRef}
        role="dialog"
        aria-label={`Edit ${chromeRef.raw}`}
        style={popoverStyle(top, left)}
      >
        <PopoverHeader title={chromeRef.raw} onClose={onClose} />
        <p style={{ fontSize: 12, color: '#6b7280', margin: '6px 0 10px' }}>{hint}</p>
        <a
          href={`${portalUrl}/admin/storefront-settings`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block',
            padding: '6px 10px',
            background: 'rgb(16, 185, 129)',
            color: 'white',
            borderRadius: 4,
            textDecoration: 'none',
            fontSize: 13,
          }}
        >
          Open in admin
        </a>
      </div>
    )
  }

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label={`Edit ${chromeRef.region} ${chromeRef.kind}`}
      style={popoverStyle(top, left)}
    >
      <PopoverHeader title={`${chromeRef.region} · ${chromeRef.kind}`} onClose={onClose} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
        {specs.map((s, i) => {
          const value = values[s.slotPath]
          if (s.kind === 'boolean') {
            return (
              <label key={s.slotPath} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={!!value}
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, [s.slotPath]: e.target.checked }))
                  }
                />
                {s.label}
              </label>
            )
          }
          const strValue = typeof value === 'string' ? value : ''
          const showPhoneHelper = s.kind === 'href' && isPhoneLike(strValue)
          return (
            <div key={s.slotPath} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>{s.label}</label>
              <input
                ref={i === 0 ? firstInputRef : undefined}
                type={s.kind === 'href' ? 'text' : 'text'}
                value={strValue}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, [s.slotPath]: e.target.value }))
                }
                placeholder={s.kind === 'href' ? '/book or https://…' : ''}
                style={{
                  border: '1px solid #d1d5db',
                  borderRadius: 4,
                  padding: '5px 8px',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  outline: 'none',
                }}
              />
              {showPhoneHelper && (
                <button
                  type="button"
                  onClick={() =>
                    setValues((prev) => ({
                      ...prev,
                      [s.slotPath]: `tel:${strValue.replace(/[^\d+]/g, '')}`,
                    }))
                  }
                  style={{
                    fontSize: 11,
                    color: 'rgb(16, 185, 129)',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  Looks like a phone — make it tel:?
                </button>
              )}
            </div>
          )
        })}
        {staleNotice ? (
          <StaleCacheNotice message={staleNotice} />
        ) : (
          <>
            {error && (
              <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }} role="alert">
                {error}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <button
                onClick={save}
                disabled={saving}
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  background: 'rgb(16, 185, 129)',
                  color: 'white',
                  borderRadius: 4,
                  border: 'none',
                  fontSize: 13,
                  cursor: saving ? 'wait' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={onClose}
                style={{
                  padding: '6px 10px',
                  background: 'white',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: 4,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </>
        )}
        <p style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
          {staleNotice ? 'Esc to close' : 'Cmd/Ctrl+S to save · Esc to close'}
        </p>
      </div>
    </div>
  )
}

function popoverStyle(top: number, left: number): React.CSSProperties {
  return {
    position: 'absolute',
    top,
    left,
    zIndex: 10000,
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
    padding: 12,
    fontSize: 13,
    fontFamily: 'system-ui, sans-serif',
    width: 280,
  }
}

function PopoverHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ fontWeight: 600, color: '#1f2937', fontSize: 13 }}>{title}</div>
      <button
        aria-label="Close"
        onClick={onClose}
        style={{
          background: 'transparent',
          border: 'none',
          color: '#9ca3af',
          fontSize: 16,
          lineHeight: 1,
          cursor: 'pointer',
          padding: 0,
        }}
      >
        ×
      </button>
    </div>
  )
}

// ─── Stale-cache notice ──────────────────────────────────────────────
//
// Save actions on /api/site-builder return `revalidate: { revalidated,
// reason? }` — the outcome of the storefront ISR tenant-tag flush. When the
// flush fails the write has still persisted; the PUBLIC site just keeps
// serving the previous version until its ISR window elapses (up to an hour).
// The editing admin's own reload can look fine (preview context), so the gap
// has to be surfaced here before the popover closes. Copy + semantics are a
// duplicate of staleCacheWarning() in
// apps/portal/src/queries/revalidateWarning.ts — the overlay ships in the
// storefront bundle and cannot import portal code; keep the two in sync on
// every edit (the portal copy is locked by tests/unit/revalidateWarning.test.ts).

type RevalidateResult = { revalidated: boolean; reason?: string }

function staleCacheWarning(revalidate: RevalidateResult | null | undefined): string | null {
  if (!revalidate || revalidate.revalidated) return null
  return `Saved, but the public site couldn't refresh automatically — visitors may see the previous version for up to an hour (${revalidate.reason ?? 'unknown'}).`
}

/** Muted-amber notice + explicit reload — the save succeeded, the cache is
 *  lagging. Informative, never alarming; the reload waits for the user so
 *  the message can actually be read. */
function StaleCacheNotice({ message }: { message: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
      <div
        role="status"
        style={{
          fontSize: 12,
          color: '#92400e',
          background: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: 4,
          padding: '6px 8px',
        }}
      >
        {message}
      </div>
      <button
        onClick={() => window.location.reload()}
        style={{
          width: '100%',
          padding: '6px 10px',
          background: 'rgb(16, 185, 129)',
          color: 'white',
          borderRadius: 4,
          border: 'none',
          fontSize: 13,
          cursor: 'pointer',
        }}
      >
        Reload page
      </button>
    </div>
  )
}

// ─── Field edit popover (Stream H / Phase F) ─────────────────────────

function FieldEditPopover({
  fieldRef,
  token,
  portalUrl,
  onClose,
}: {
  fieldRef: FieldEditRef
  token: string
  portalUrl: string
  onClose: () => void
}) {
  const { binding } = fieldRef
  // Seeded from the DOM only as a FALLBACK. `innerText` is a lossy
  // projection of the stored source for any field the renderer
  // transforms — a markdown body renders `## Heading` as "Heading" and
  // `[label](/href)` as "label" — and this value is PUT back verbatim on
  // save, so seeding from the DOM silently flattened the source on the
  // first edit. The effect below replaces it with the stored value.
  const [value, setValue] = useState<string | boolean>(() => {
    if (binding.type === 'switch') return false
    return fieldRef.element.innerText?.trim() ?? ''
  })
  const [loading, setLoading] = useState(binding.type !== 'switch')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [staleNotice, setStaleNotice] = useState<string | null>(null)
  const firstInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)

  const rect = fieldRef.element.getBoundingClientRect()
  const top = Math.max(8, rect.bottom + window.scrollY + 6)
  const left = Math.max(8, Math.min(rect.left + window.scrollX, window.innerWidth - 320))

  // Pull the stored value for this field. On failure we keep the DOM
  // seed and say so — a degraded editor beats a dead popover, but the
  // admin needs to know the box may not hold the true source.
  useEffect(() => {
    if (binding.type === 'switch') return
    let cancelled = false
    void (async () => {
      try {
        const res = await fetch(siteBuilderEndpoint(portalUrl, 'content-page-field-read'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'content-page-field-read',
            token,
            page_id: fieldRef.pageId,
            section_idx: fieldRef.sectionIdx,
            field_path: fieldRef.fieldPath,
          }),
        })
        const json = (await res.json()) as { value?: unknown; error?: string }
        if (cancelled) return
        if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`)
        // null is an absent optional field — an empty editor is the
        // correct seed for it, not the rendered placeholder text.
        setValue(json.value == null ? '' : String(json.value))
      } catch {
        if (!cancelled) {
          setError('Could not load the saved text — showing the text on the page instead.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [binding.type, portalUrl, token, fieldRef])

  useEffect(() => {
    if (loading) return
    firstInputRef.current?.focus()
    firstInputRef.current?.select?.()
  }, [loading])

  const save = useCallback(async () => {
    // `loading` is part of the guard on purpose: until the stored value
    // lands, `value` still holds the lossy DOM seed, and a fast Cmd+S
    // would persist exactly the flattening this fetch exists to prevent.
    if (saving || loading || staleNotice) return
    setSaving(true)
    setError(null)
    try {
      const res = await fetch(siteBuilderEndpoint(portalUrl, 'content-page-field-patch'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'content-page-field-patch',
          token,
          page_id: fieldRef.pageId,
          patches: [
            {
              section_idx: fieldRef.sectionIdx,
              field_path: fieldRef.fieldPath,
              value,
            },
          ],
        }),
      })
      const json = (await res.json()) as { error?: string; revalidate?: RevalidateResult }
      if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`)
      const stale = staleCacheWarning(json.revalidate)
      if (stale) {
        // Patch persisted; hold the popover open so the admin reads the
        // cache gap before triggering the reload themselves.
        setStaleNotice(stale)
        return
      }
      onClose()
      window.location.reload()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }, [saving, loading, staleNotice, value, token, portalUrl, fieldRef, onClose])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault()
        void save()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [save, onClose])

  function renderControl() {
    if (binding.type === 'switch') {
      return (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => setValue(e.target.checked)}
          />
          Enabled
        </label>
      )
    }
    if (binding.type === 'select') {
      return (
        <select
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => setValue(e.target.value)}
          style={popoverInputStyle()}
        >
          {binding.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      )
    }
    if (binding.type === 'media') {
      return (
        <div style={{ fontSize: 12, color: '#6b7280' }}>
          Media picker:{' '}
          <a
            href={`${portalUrl}/admin/media-library?picker=1&pageId=${fieldRef.pageId}&sectionIdx=${fieldRef.sectionIdx}&fieldPath=${encodeURIComponent(fieldRef.fieldPath)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'rgb(59, 130, 246)' }}
          >
            Open Media Library
          </a>
          <p style={{ fontSize: 11, marginTop: 6, color: '#9ca3af' }}>
            Picking a media item in the portal applies it to this field.
          </p>
        </div>
      )
    }
    if (binding.type === 'url') {
      return (
        <input
          ref={(r) => {
            firstInputRef.current = r
          }}
          type="text"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => setValue(e.target.value)}
          placeholder="/contact or https://..."
          style={popoverInputStyle()}
        />
      )
    }
    // text / number
    if (binding.type === 'text' && binding.multiline) {
      return (
        <textarea
          ref={(r) => {
            firstInputRef.current = r
          }}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => setValue(e.target.value)}
          rows={4}
          maxLength={binding.max}
          style={{ ...popoverInputStyle(), resize: 'vertical', minHeight: 64 }}
        />
      )
    }
    return (
      <input
        ref={(r) => {
          firstInputRef.current = r
        }}
        type="text"
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => setValue(e.target.value)}
        maxLength={binding.type === 'text' ? binding.max : undefined}
        style={popoverInputStyle()}
      />
    )
  }

  return (
    <div
      role="dialog"
      aria-label={`Edit ${fieldRef.fieldPath}`}
      style={popoverStyle(top, left)}
    >
      <PopoverHeader title={fieldRef.fieldPath.replace(/^data\./, '')} onClose={onClose} />
      <div style={{ marginTop: 8 }}>{renderControl()}</div>
      {staleNotice ? (
        <StaleCacheNotice message={staleNotice} />
      ) : (
        <>
          {error && (
            <div style={{ fontSize: 12, color: '#dc2626', marginTop: 6 }} role="alert">
              {error}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button
              onClick={save}
              disabled={saving || loading || binding.type === 'media'}
              style={{
                flex: 1,
                padding: '6px 10px',
                background: 'rgb(59, 130, 246)',
                color: 'white',
                borderRadius: 4,
                border: 'none',
                fontSize: 13,
                cursor: saving ? 'wait' : 'pointer',
                opacity: saving || binding.type === 'media' ? 0.5 : 1,
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={onClose}
              style={{
                padding: '6px 10px',
                background: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </>
      )}
      <p style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
        {staleNotice ? 'Esc to close' : 'Cmd/Ctrl+S to save · Esc to close'}
      </p>
    </div>
  )
}

function popoverInputStyle(): React.CSSProperties {
  return {
    border: '1px solid #d1d5db',
    borderRadius: 4,
    padding: '5px 8px',
    fontSize: 13,
    fontFamily: 'inherit',
    outline: 'none',
    width: '100%',
  }
}
